import { ReportsView } from '@eriko/ui/src/reports';
import { toast } from 'sonner';
import { useOrgReports } from '../../../hooks/useOrgReports';
import { useOrgDetail } from '../../../hooks/useReview';

/**
 * Reviewer's per-client preview of what the SME sees in their Reports page.
 *
 * Per PRD §5.2: "Reports — Same reports as SME sees (for reference)." This is
 * NOT a separate analytical surface — power-user reports live in the Reviewer
 * AI Chat (advanced-reporting pack: AR/AP aging, KPI, cashflow, ratios, etc.).
 * The reviewer's job here is to sanity-check what the SME will see before
 * sign-off: are category mix and tax estimate reasonable, are there outliers.
 *
 * Differences from the SME Reports page:
 *   - Scoped to the chosen org via `?orgId=` (admin JWT is `__internal__`).
 *   - Send Email omitted — emailing a reviewer themselves an SME's books is
 *     the wrong default.
 *   - Masthead VOL line carries the client name so the reviewer always knows
 *     which client they're looking at without checking the sidebar.
 *   - Outer `bg-paper` wrapper dropped — the OrgDetailPage tab container has
 *     its own background; the cream paper would otherwise float in white.
 */
export function ReportsTab({ orgId }: { orgId: string }) {
  const { data: org } = useOrgDetail(orgId);
  const {
    period,
    setPeriod,
    customRange,
    applyCustomRange,
    periodLabels,
    visiblePeriods,
    periodLabel,
    summary,
    categories,
    taxEstimate,
    trend,
    transactions,
    isLoading,
    isPlaceholderData,
    isFetching,
    error,
    downloadPdf,
    isDownloadingPdf,
    downloadCsv,
    isDownloadingCsv,
  } = useOrgReports(orgId);

  const loading = isLoading || isPlaceholderData;
  const refreshing = isFetching && !loading;

  const handleDownloadPdf = async () => {
    if (isDownloadingPdf) return;
    try {
      const filename = await downloadPdf();
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Couldn't download the report. Please try again.",
      );
    }
  };

  const handleDownloadCsv = async () => {
    if (isDownloadingCsv) return;
    try {
      const filename = await downloadCsv();
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Couldn't download the report. Please try again.",
      );
    }
  };

  const clientLabel = (org?.name ?? 'Client').toUpperCase();
  const mastheadVolume = `REVIEWER PREVIEW · ${clientLabel} · ${periodLabel.toUpperCase()}`;

  return (
    <ReportsView
      period={period}
      setPeriod={setPeriod}
      customRange={customRange}
      applyCustomRange={applyCustomRange}
      periodLabels={periodLabels}
      visiblePeriods={visiblePeriods}
      periodLabel={periodLabel}
      summary={summary}
      categories={categories}
      taxEstimate={taxEstimate}
      trend={trend}
      transactions={transactions}
      loading={loading}
      refreshing={refreshing}
      error={error}
      onDownloadPdf={handleDownloadPdf}
      onDownloadCsv={handleDownloadCsv}
      isDownloadingPdf={isDownloadingPdf}
      isDownloadingCsv={isDownloadingCsv}
      mastheadVolume={mastheadVolume}
      subtitle="Exactly what the client sees in their Reports page. Use this to sanity-check categorizations and the tax estimate before sign-off."
      outerClassName=""
    />
  );
}
